export type FilterOperator = ":" | "!:" | "=" | "!=" | ">=" | "<=" | ">" | "<";

export type FilterPropertyKind = "text" | "enum" | "date" | "number";

export interface FilterPropertyOption {
  value: string;
  label: string;
}

export interface FilterProperty {
  key: string;
  label: string;
  kind: FilterPropertyKind;
  operators?: readonly FilterOperator[];
  defaultOperator?: FilterOperator;
  options?: readonly FilterPropertyOption[];
  loadOptions?: (query: string) => Promise<FilterPropertyOption[]>;
  /** When true/false, overrides the default multi-value rules for this property. */
  allowMultiple?: boolean;
  /** Lower bound for `kind: "number"` inputs (e.g. 0 for counts). */
  min?: number;
  /** When true, the number input accepts whole non-negative digits only. */
  integer?: boolean;
}

export interface PropertyFilterToken {
  id: string;
  propertyKey?: string;
  operator: FilterOperator;
  value: string;
  valueLabel?: string;
}

export interface PropertyFilterQuery {
  operation: "and" | "or";
  tokens: PropertyFilterToken[];
}

/** Snapshot of the active Bookings PropertyFilter query, forwarded to Kai. */
export interface KaiFilterContext {
  page: "bookings";
  operation: "and" | "or";
  active_tokens: { property_key: string; operator: string; value: string }[];
}

/** A single PropertyFilter token Kai wants to add to a table. */
export interface KaiTableFilterToken {
  property_key: string;
  /** Defaults to ":" for text fields and "=" for enum/date/number fields. */
  operator?: string;
  value: string;
}

/**
 * Patch Kai emits via the `apply_table_filters` SSE event. Applied on the
 * frontend only when the user is on `/bookings`.
 */
export interface KaiTableFiltersPatch {
  mode?: "merge" | "replace" | "clear";
  operation?: "and" | "or";
  tokens?: KaiTableFilterToken[];
  token_groups?: { operation: "and" | "or"; tokens: KaiTableFilterToken[] }[];
}

/** Window-event payload after Kai writes table filters to sessionStorage. */
export interface KaiTableFiltersEventDetail {
  page: "bookings";
  storageKey: string;
  query: PropertyFilterQuery;
}

export const EMPTY_PROPERTY_FILTER_QUERY: PropertyFilterQuery = {
  operation: "and",
  tokens: [],
};

export const DEFAULT_OPERATORS: Record<FilterPropertyKind, FilterOperator[]> = {
  text: [":", "!:", "=", "!="],
  enum: ["=", "!="],
  date: [">=", "<=", "=", ">", "<"],
  number: [">=", "<=", "="],
};

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  ":": "contains",
  "!:": "does not contain",
  "=": "equals",
  "!=": "does not equal",
  ">=": "on or after",
  "<=": "on or before",
  ">": "after",
  "<": "before",
};

export const NUMBER_OPERATOR_LABELS: Record<FilterOperator, string> = {
  ":": "contains",
  "!:": "does not contain",
  "=": "equals",
  "!=": "does not equal",
  ">=": "at least",
  "<=": "at most",
  ">": "greater than",
  "<": "less than",
};

export function operatorLabel(
  operator: FilterOperator,
  kind?: FilterPropertyKind,
): string {
  if (kind === "number") return NUMBER_OPERATOR_LABELS[operator] ?? operator;
  return OPERATOR_LABELS[operator] ?? operator;
}
